import { defineStore } from 'pinia';
import { resolveKey } from 'src/utils/user_access';
import { EnumType, EnumWithType } from 'src/core/types/enum';
import { WritableComputedRef, computed } from 'vue';
import { useUserStore } from 'src/core/stores/user';
import { CountryCommodityPermissionType, ROLE_IDS } from 'src/modules/userAccess/types';

type PermissionStore = {
  access_rights: any;
  permissions: WritableComputedRef<string[]>;
  role_ids: WritableComputedRef<number[]>;
};

/**
 * Permission Store
 */
export const usePermissionStore = defineStore('permission', {
  state: () =>
    ({
      access_rights: computed({
        get() {
          return useUserStore().user?.access_rights;
        },
        set(val) {
          const userStore = useUserStore();
          userStore.user?.access_rights
            ? (userStore.user.access_rights = val)
            : null;
        },
      }), // we are using computed to link access_rights directly from user store
      permissions: computed({
        get() {
          return useUserStore().user?.permissions ?? [];
        },
        set(val: string[]) {
          const userStore = useUserStore();
          userStore.user?.permissions
            ? (userStore.user.permissions = val)
            : null;
        },
      }), // we are using computed to link permissions directly from user store
    role_ids: computed({
      get() {
        return useUserStore().user?.role_ids ?? [];
      },
      set(val: number[]) {
        const userStore = useUserStore();
        userStore.user?.role_ids ? (userStore.user.role_ids = val) : null;
      },
    }), // we are using computed to link role_ids directly from user store
    } as PermissionStore),

  getters: {
    /**
     * Checks if the user has the specified permission.
     * @param permission - The permission to check.
     * @returns A boolean indicating whether the user has the permission.
     */
    checkUserAccess: (state) => (permission: string) => {
      if (!useUserStore().access_token) return false;
      if (state.permissions.includes(permission)) {
        return state.permissions.includes(permission) ?? false;
      } else if (state.permissions.includes('admin')) {
        return true;
      } else {
        return resolveKey(permission, state.access_rights) !== null;
      }
    },

    /**
     * Creates a permission store using Vuex's defineStore function.
     * @param name - The name of the store.
     * @param options - The options object for the store.
     * @returns The created permission store.
     */
    returnUserAccess: (state) => (permission: string) => {
      return resolveKey(permission, state.access_rights);
    },

    checkReclassificationAccess: (state) => {
      if (state.permissions.includes('admin')) return true;
      return useUserStore().user?.reclassification_access ?? false;
    },

    returnCommoditiesAccess: (state) => (type: 'read' | 'create') => {
      const userStore = useUserStore();
      const permissionStore = usePermissionStore();
      if (state.permissions.includes('admin'))
        return userStore.getCustomerCommodities;
      return permissionStore.checkPropertyAccess<CountryCommodityPermissionType>(
        userStore.getCustomerCommodities,
        'commodity',
        permissionStore.returnUserAccess(`dataset.${type}`)
      ) as EnumWithType[];
    },

    checkRoleAccess: (state) => (role: number): boolean => {
      if (state?.role_ids?.length === 0) return false;
      if (state?.role_ids?.includes(ROLE_IDS.MERIDIA_ADMIN) || state?.permissions?.includes('admin')) return true;
      return state?.role_ids?.includes(role);
    },
  },

  actions: {
    /**
     * Checks property access based on permissions.
     * @param enums - An array of EnumType objects.
     * @param key - The key of the property to check access for.
     * @param permissions - An array of objects containing permissions.
     * @returns A promise that resolves to an array of EnumType objects representing the allowed values.
     */
    checkPropertyAccess<T extends Record<keyof T, Array<string>>>(
      enums: EnumType[],
      key: keyof T,
      permissions: T[]
    ): EnumType[] {
      if (
        !this.access_rights ||
        (Object.keys(this.access_rights).length === 0 &&
          this.access_rights.constructor === Object)
      ) {
        return enums;
      }
      if (String(permissions[0]) == '*') return enums;
      const values = enums?.map((enumItem) => enumItem.value);
      const allowedValues = values
        ?.map((value) => {
          if (
            permissions.some((entry) => {
              return entry[key].includes('*') || entry[key].includes(value);
            })
          ) {
            return enums.find((enumItem) => enumItem.value === value);
          }
        })
        .filter((value) => value !== undefined) as EnumType[];
      return allowedValues;
    },

    /**
     * Checks property access for a specific value.
     *
     * @template T - The type of the permissions array.
     * @param {EnumType[]} enums - The array of EnumType objects.
     * @param {keyof T} key - The key of the permissions array.
     * @param {T[]} permissions - The array of permissions.
     * @param {string} valueToCheck - The value to check.
     * @param {keyof T} keyToCheck - The key to check.
     * @returns {Promise<EnumType[]>} - The array of allowed EnumType objects.
     */
    async checkPropertyAccessForSpecificValue<
      T extends Record<keyof T, Array<string>>
    >(
      enums: EnumType[],
      key: keyof T,
      permissions: T[],
      valueToCheck: string,
      keyToCheck: keyof T
    ): Promise<EnumType[]> {
      if (
        !this.access_rights ||
        (Object.keys(this.access_rights).length === 0 &&
          this.access_rights.constructor === Object)
      ) {
        return enums;
      }
      if (String(permissions[0]) == '*') return enums;

      const values = enums?.map((enumItem) => enumItem.value);
      const allowedValues = values
        ?.map((value) => {
          if (
            permissions.some((entry) => {
              return (
                (entry[key].includes('*') &&
                  entry[keyToCheck].includes(valueToCheck)) ||
                (entry[key].includes(value) &&
                  entry[keyToCheck].includes(valueToCheck)) ||
                (entry[key].includes(value) &&
                  entry[keyToCheck].includes('*')) ||
                (entry[key].includes('*') && entry[keyToCheck].includes('*'))
              );
            })
          ) {
            return enums.find((enumItem) => enumItem.value === value);
          }
        })
        .filter((value) => value !== undefined) as EnumType[];
      return allowedValues;
    },

    checkIfAnyUserAccess(access_rights: any): 'ALL' | 'SOME' | 'NONE' {
      const createIsValid =
        access_rights?.dataset?.create &&
        Array.isArray(access_rights.dataset.create) &&
        access_rights.dataset.create[0];
      if (
        !access_rights ||
        (createIsValid && access_rights.dataset.create[0] == '*')
      )
        return 'ALL';
      else if (
        createIsValid &&
        access_rights.dataset.create[0].commodity?.length === 0 &&
        access_rights.dataset.create[0].country?.length === 0
      )
        return 'NONE';
      else if (access_rights?.dataset?.create?.length > 0) return 'SOME';
      else return 'NONE';
    },
  },
});
